import {
  Badge,
  Box,
  Flex,
  IconButton,
  Text,
  useColorModeValue,
  useColorMode,
  Tooltip,
  HStack,
} from "@chakra-ui/react";
import { FiSun, FiMoon, FiUser, FiLogOut, FiSettings, FiLogIn, FiLock } from "react-icons/fi";
import { SettingsIcon } from "components/Icons/Icons";
import SidebarResponsive from "components/Sidebar/SidebarResponsive";
import PropTypes from "prop-types";
import React from "react";
import { NavLink } from "react-router-dom";
import { getSidebarRoutes } from "routes";
import { getRole, isTokenValid, logout } from "utils/auth";

export default function HeaderLinks(props) {
  const { variant, children, fixed, secondary, onOpen, ...rest } = props;
  const { colorMode, toggleColorMode } = useColorMode();
  const authenticated = isTokenValid();
  const userRole = authenticated ? getRole() : null;
  const routes = getSidebarRoutes();

  const navbarIcon = useColorModeValue("slate.600", "slate.200");
  const bgHover = useColorModeValue("slate.100", "whiteAlpha.200");

  const getRoleColorScheme = (role) => {
    switch (role) {
      case "admin":
        return "purple";
      case "moderator":
        return "blue";
      case "user":
      default:
        return "teal";
    }
  };

  return (
    <Flex
      alignItems="center"
      flexDirection="row"
      gap={{ base: "6px", sm: "8px", md: "12px" }}
    >
      {/* Role Indicator Badge (Logged-in users) */}
      {authenticated && userRole && (
        <Badge
          colorScheme={getRoleColorScheme(userRole)}
          variant="solid"
          borderRadius="full"
          px={{ base: "6px", sm: "10px" }}
          py="2px"
          fontSize={{ base: "2xs", sm: "xs" }}
          fontWeight="800"
          letterSpacing="0.05em"
          textTransform="uppercase"
        >
          {userRole}
        </Badge>
      )}

      {/* Dark/Light Mode Toggle */}
      <Tooltip label={colorMode === "light" ? "Dark Mode" : "Light Mode"} hasArrow placement="bottom">
        <IconButton
          aria-label="Toggle Color Mode"
          icon={colorMode === "light" ? <FiMoon size="16" /> : <FiSun size="16" />}
          onClick={toggleColorMode}
          variant="ghost"
          borderRadius="full"
          color={navbarIcon}
          _hover={{ bg: bgHover }}
          size="sm"
        />
      </Tooltip>

      {/* Settings Drawer Button */}
      <Tooltip label="Settings" hasArrow placement="bottom">
        <IconButton
          aria-label="Open Settings"
          display={{ base: "none", sm: "inline-flex" }}
          icon={<SettingsIcon w="16px" h="16px" color={navbarIcon} />}
          onClick={props.onOpen}
          variant="ghost"
          borderRadius="full"
          _hover={{ bg: bgHover }}
          size="sm"
        />
      </Tooltip>

      {/* Public Visitor: Member Access Button */}
      {!authenticated ? (
        <NavLink to="/auth/signin">
          <Box
            as="button"
            display="flex"
            alignItems="center"
            gap="5px"
            bg="teal.500"
            color="white"
            px={{ base: "10px", sm: "12px", md: "16px" }}
            py={{ base: "5px", sm: "6px", md: "7px" }}
            borderRadius="full"
            fontSize={{ base: "xs", md: "xs" }}
            fontWeight="700"
            transition="all 0.2s"
            _hover={{ bg: "teal.600", transform: "translateY(-1px)" }}
            boxShadow="0 2px 8px rgba(13, 148, 136, 0.35)"
          >
            <FiLock size="12" />
            <Box as="span" display={{ base: "none", md: "inline" }}>Member Access</Box>
            <Box as="span" display={{ base: "inline", md: "none" }}>Login</Box>
          </Box>
        </NavLink>
      ) : (
        <>
          {/* User Profile Navigation */}
          <Tooltip label="My Profile" hasArrow placement="bottom">
            <NavLink to="/txd/profile">
              <Box position="relative">
                <IconButton
                  aria-label="User Profile"
                  icon={<FiUser size="16" />}
                  variant="ghost"
                  borderRadius="full"
                  color={navbarIcon}
                  _hover={{ bg: bgHover }}
                  size="sm"
                />
                <Box
                  position="absolute"
                  top="2px"
                  right="2px"
                  w="8px"
                  h="8px"
                  borderRadius="full"
                  bg="emerald.400"
                  border="2px solid"
                  borderColor={useColorModeValue("white", "navy.700")}
                />
              </Box>
            </NavLink>
          </Tooltip>

          {/* Logout Button */}
          <Tooltip label="Logout" hasArrow placement="bottom">
            <IconButton
              aria-label="Logout"
              icon={<FiLogOut size="16" />}
              onClick={logout}
              variant="ghost"
              colorScheme="red"
              borderRadius="full"
              size="sm"
            />
          </Tooltip>
        </>
      )}

      {/* Mobile Responsive Sidebar Switcher */}
      <SidebarResponsive
        logoText={props.logoText}
        secondary={props.secondary}
        routes={routes}
        {...rest}
      />
    </Flex>
  );
}

HeaderLinks.propTypes = {
  variant: PropTypes.string,
  fixed: PropTypes.bool,
  secondary: PropTypes.bool,
  onOpen: PropTypes.func,
};


